import { toast } from 'sonner'
import api from '@/services/api'
import type { Deal, ComboVariationWithPricing, Proposta, PropostaItem } from '@/types'
import { getFriendlyError } from '@/utils/getFriendlyError'
import {
  generateAnnexPdf,
  generateComboVariationPdf,
  generateMultiItemAnnexPdf,
  type AnnexContractedItem,
} from '@/utils/pdfExport'
import {
  buildLoyaltyLabelFromItem,
  buildInvestmentLabelFromItem,
  buildPaymentDetailsFromItem,
} from '@/utils/proposalExport'
import { hasExplicitComboProposal } from '@/utils/proposalExportComboSlides'
import { isDesenvolvimentoPlan, type CatalogPlan } from '@/utils/planCatalog'
import { generateComboVariationPpt } from '@/utils/pptxExport'
import { downloadLinkedProposalPptx } from '@/utils/downloadLinkedProposalPptx'
import { normalizePropostaForExport, resolvePropostaItems } from '@/utils/normalizeProposta'
import { applyCatalogToProposal } from './proposalTabCatalog'
import { convertPropostaToPlanData } from './proposalTabPlanData'

const exportToastStyle = {
  background: 'var(--bg-card)',
  border: '1px solid var(--glass-border)',
  color: 'var(--text-primary)',
} as const

function exportToastLoading(message: string) {
  return toast.loading(message, { style: exportToastStyle })
}

function proposalHasMultipleItems(proposalItens: PropostaItem[]): boolean {
  return proposalItens.length > 1
}

export type ProposalExportContext = {
  deal: Deal
  proposals: Proposta[]
  editingId: string | null
  draftItens: PropostaItem[]
  catalogPlans: CatalogPlan[]
}

export async function resolveProposalForExport(
  ctx: ProposalExportContext,
  proposalToExport?: Proposta,
): Promise<Proposta | null> {
  let raw = proposalToExport ?? ctx.proposals.find((p) => p.id === ctx.editingId) ?? ctx.proposals[0]
  if (!raw) return null

  if (raw.id) {
    try {
      const { data } = await api.get(`/propostas/${raw.id}`)
      raw = (data.data ?? data) as Proposta
    } catch {
      /* usa cópia em memória */
    }
  }

  const normalized = normalizePropostaForExport(raw)
  const effectiveItems =
    resolvePropostaItems(normalized).length > 0
      ? resolvePropostaItems(normalized)
      : ctx.draftItens.length > 0
        ? ctx.draftItens
        : []

  if (effectiveItems.length === 0) return null

  return applyCatalogToProposal(
    { ...normalized, itens: effectiveItems, items: effectiveItems },
    ctx.catalogPlans,
  )
}

const EMPTY_EXPORT_MSG =
  'Nenhum plano/item na proposta. Adicione um plano, salve o rascunho e exporte de novo.'

function buildAnnexContractedItems(
  proposal: Proposta,
  catalogPlans: CatalogPlan[],
): AnnexContractedItem[] {
  return resolvePropostaItems(proposal).map((item) => ({
    nome: item.descricao || item.nome || 'Serviço',
    loyalty: isDesenvolvimentoPlan(item, catalogPlans)
      ? (() => {
          const months = item.duration || item.pricing?.installments || item.pricing?.duracao
          return months ? `Prazo: ${months} meses` : 'Conforme cronograma do projeto'
        })()
      : buildLoyaltyLabelFromItem(item),
    payment: buildPaymentDetailsFromItem(item),
    investment: buildInvestmentLabelFromItem(item),
  }))
}

export function createProposalExportHandlers(ctx: ProposalExportContext) {
  const exportPPTX = async (proposalToExport?: Proposta) => {
    const toastId = exportToastLoading('Gerando apresentação PPTX...')
    try {
      const targetProposal = await resolveProposalForExport(ctx, proposalToExport)
      if (!targetProposal) {
        toast.error(EMPTY_EXPORT_MSG, { id: toastId })
        return
      }
      await downloadLinkedProposalPptx(targetProposal, ctx.deal.empresaNome, {
        catalogPlans: ctx.catalogPlans,
        dealServico: ctx.deal.servico,
      })
      toast.success('Apresentação baixada!', { id: toastId })
    } catch (err: unknown) {
      console.error(err)
      toast.error(getFriendlyError(err, 'Erro ao baixar apresentação'), { id: toastId })
    }
  }

  const exportPDF = async (proposalToExport?: Proposta) => {
    const toastId = exportToastLoading('Gerando Anexo 1...')
    try {
      const targetProposal = await resolveProposalForExport(ctx, proposalToExport)
      if (!targetProposal) {
        toast.error(EMPTY_EXPORT_MSG, { id: toastId })
        return
      }
      const proposalItens = targetProposal.itens || targetProposal.items || []
      const contractedItems = buildAnnexContractedItems(targetProposal, ctx.catalogPlans)
      const exportOptions = {
        catalogPlans: ctx.catalogPlans,
        dealServico: ctx.deal.servico,
      }
      const pdfBlob =
        proposalHasMultipleItems(proposalItens) && !hasExplicitComboProposal(targetProposal)
          ? await generateMultiItemAnnexPdf(targetProposal, ctx.deal.empresaNome, contractedItems, exportOptions)
          : await generateAnnexPdf(
              convertPropostaToPlanData(targetProposal, ctx.deal.servico, ctx.catalogPlans),
              ctx.deal.empresaNome,
              contractedItems,
            )
      const url = URL.createObjectURL(pdfBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = `anexo-1-${ctx.deal.empresaNome}.pdf`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('Anexo 1 baixado!', { id: toastId })
    } catch (err: unknown) {
      console.error(err)
      toast.error(getFriendlyError(err, 'Erro ao baixar Anexo 1'), { id: toastId })
    }
  }

  const exportVariationsPPTX = async (variationsWithPricing: ComboVariationWithPricing[]) => {
    const toastId = exportToastLoading('Gerando PPTX multi-opção...')
    try {
      await generateComboVariationPpt(variationsWithPricing, ctx.deal.empresaNome)
      toast.success(`${variationsWithPricing.length + 1} slides gerados!`, { id: toastId })
    } catch (err: unknown) {
      console.error(err)
      toast.error(getFriendlyError(err, 'Erro ao gerar PPTX'), { id: toastId })
    }
  }

  const exportVariationsPDF = async (variationsWithPricing: ComboVariationWithPricing[]) => {
    const toastId = exportToastLoading('Gerando PDF multi-opção...')
    try {
      const pdfBlob = await generateComboVariationPdf(variationsWithPricing, ctx.deal.empresaNome)
      const url = URL.createObjectURL(pdfBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = `proposta-opcoes-${ctx.deal.empresaNome}-${Date.now()}.pdf`
      a.click()
      URL.revokeObjectURL(url)
      toast.success('PDF multi-opção baixado!', { id: toastId })
    } catch (err: unknown) {
      console.error(err)
      toast.error(getFriendlyError(err, 'Erro ao gerar PDF'), { id: toastId })
    }
  }

  return { exportPPTX, exportPDF, exportVariationsPPTX, exportVariationsPDF }
}
